import { Pill } from "../../components/ui";
import { EMPTY_FILTER, type TrackerFilter, type TrackerReport } from "../../api/tracker";
import { SITE_STATUSES } from "./columns";


type Options = TrackerReport["filters"];

/** The toolbar over the table: a search, the status pills and the two
 * selects the office reaches for. The site list's rarer filters (contract,
 * valve make, DN range, assigned, flagged) still narrow the report and the
 * download, folded under "More filters" so they do not crowd the first row. */
export function TrackerFilters({
  filter,
  search,
  options,
  onSearch,
  onChange,
}: {
  filter: TrackerFilter;
  /** What is typed, which reaches `filter.q` once the typing pauses. */
  search: string;
  options: Options | undefined;
  onSearch: (text: string) => void;
  onChange: (filter: TrackerFilter) => void;
}) {
  function set<K extends keyof TrackerFilter>(key: K, value: TrackerFilter[K]) {
    onChange({ ...filter, [key]: value });
  }
  const narrowed = search !== "" || JSON.stringify({ ...filter, q: "" }) !== JSON.stringify(EMPTY_FILTER);
  const clusters = (options?.clusters ?? []).filter(
    (c) => filter.contract_id === "" || String(c.contract_id) === filter.contract_id,
  );

  return (
    <div className="tracker-filters-bar">
      <div className="toolbar">
        <input
          type="search"
          className="search-input"
          placeholder="Search site or works order"
          aria-label="Search site or works order"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
        <div className="pill-row" role="group" aria-label="Filter by status">
          {SITE_STATUSES.map(({ value, label }) => {
            const on = filter.status.includes(value);
            return (
              <Pill
                key={value}
                on={on}
                onToggle={() => set("status", on ? filter.status.filter((s) => s !== value) : [...filter.status, value])}
              >
                {label}
              </Pill>
            );
          })}
        </div>
        <select
          className="select"
          aria-label="Cluster"
          value={filter.cluster_id}
          onChange={(e) => set("cluster_id", e.target.value)}
        >
          <option value="">All clusters</option>
          {clusters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className="select"
          aria-label="Provider"
          value={filter.provider_id}
          onChange={(e) => set("provider_id", e.target.value)}
        >
          <option value="">All providers</option>
          {(options?.providers ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {narrowed ? (
          <button
            type="button"
            className="go-link"
            onClick={() => {
              onSearch("");
              onChange(EMPTY_FILTER);
            }}
          >
            Clear filters
          </button>
        ) : null}
      </div>
      <details className="tracker-more">
        <summary>More filters</summary>
        <div className="tracker-more-body">
          <label className="field">
            Contract
            <select value={filter.contract_id} onChange={(e) => set("contract_id", e.target.value)}>
              <option value="">All</option>
              {(options?.contracts ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Valve make
            <input value={filter.valve_make} onChange={(e) => set("valve_make", e.target.value)} />
          </label>
          <label className="field">
            DN from
            <input inputMode="numeric" value={filter.dn_min} onChange={(e) => set("dn_min", e.target.value)} />
          </label>
          <label className="field">
            DN to
            <input inputMode="numeric" value={filter.dn_max} onChange={(e) => set("dn_max", e.target.value)} />
          </label>
          {/* The values the site filter parses; anything else reads as "any". */}
          <label className="field">
            Assigned
            <select value={filter.assigned} onChange={(e) => set("assigned", e.target.value)}>
              <option value="">Any</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </label>
          {/* The server reads `flagged` as a switch -- present is on -- so it
              is a checkbox: a "Not flagged" option would have meant flagged. */}
          <label className="check">
            <input
              type="checkbox"
              checked={filter.flagged !== ""}
              onChange={(e) => set("flagged", e.target.checked ? "1" : "")}
            />
            Flagged only
          </label>
        </div>
      </details>
    </div>
  );
}
